/**
 * XSS prevention utilities.
 *
 * Centralises input sanitisation so every form / URL parameter goes through
 * a single, auditable code-path before it is used in navigation or rendered.
 */

/**
 * Allowed internal path prefixes.  Any `backUrl` / `backURL` value that does
 * NOT start with one of these strings is rejected and replaced with the
 * fallback so we cannot be tricked into redirecting the user to an arbitrary
 * external site (open-redirect → phishing / credential-theft).
 */
const ALLOWED_INTERNAL_PATHS = [
  '/', // catches all relative paths
];

/**
 * Validate a redirect URL coming from an untrusted source (e.g. a query-string
 * parameter).  Only relative paths that start with '/' are accepted.
 * Absolute URLs, `javascript:` URIs and protocol-relative `//` URLs are all
 * rejected.
 *
 * @param url      - the candidate redirect value (may be null)
 * @param fallback - value returned when validation fails (default '/')
 */
export function sanitizeRedirectUrl(url: string | null | undefined, fallback = '/'): string {
  if (!url) return fallback;

  try {
    // Decode any percent-encoding so encoded `javascript:` cannot sneak through
    const decoded = decodeURIComponent(url);

    // Reject anything that contains a protocol / scheme
    // (covers `javascript:`, `data:`, `http:`, `https:`, `//` etc.)
    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/i.test(decoded)) return fallback;
    if (decoded.startsWith('//')) return fallback;

    // Must start with one of the explicitly allowed prefixes
    const isAllowed = ALLOWED_INTERNAL_PATHS.some((prefix) => decoded.startsWith(prefix));
    if (!isAllowed) return fallback;

    return decoded;
  } catch {
    // decodeURIComponent threw – the value is malformed
    return fallback;
  }
}

/**
 * Strip characters that are dangerous inside HTML attribute values and text
 * nodes.  Use this for any user-supplied string that ends up in a visible
 * label, title, or aria attribute.
 *
 * NOTE: React already escapes text rendered inside JSX expressions, so this is
 * primarily a defence-in-depth measure for edge-cases (e.g. values passed to
 * third-party libraries, dynamic `title` attributes built by string
 * concatenation, etc.).
 */
export function sanitizeText(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}
